import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";

// Only NotFoundPage loads immediately (small, needed for 404)
import NotFoundPage from "./pages/NotFoundPage";

// Login and all other pages – lazy loaded (reduces initial JS; LoginPage ~4 KiB when on /home etc.)
const LoginPage = lazy(() => import("./pages/LoginPage"));

// Admin & content pages – lazy loaded (reduces initial JS by ~500 KiB)
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Historique = lazy(() => import("./pages/Historique"));
const SeminairesPage = lazy(() => import("./pages/SeminairePage"));
const RestaurantsAndMenusPage = lazy(() => import("./pages/RestaurantsAndMenusPage"));
const BoissonsPage = lazy(() => import("./pages/BoissonsPage"));
const SpasPage = lazy(() => import("./pages/SpasPage"));
const LoisirsPage = lazy(() => import("./pages/LoisirsPage"));
const OffresPage = lazy(() => import("./pages/OffresPage"));
const EvenementsPage = lazy(() => import("./pages/EvenementsPage"));
const RoomServicePage = lazy(() => import("./pages/RoomServicePage"));
const SkyLoungePage = lazy(() => import("./pages/SkyLoungePage"));
const Statistiques = lazy(() => import("./pages/Statistiques"));
const PageContents = lazy(() => import("./pages/page-contents"));
const AproposAdminPage = lazy(() => import("./pages/AproposAdminPage"));
const QuestionnaireClient = lazy(() => import("./pages/QuestionnaireClient"));
const QuestionnairesPage = lazy(() => import("./pages/QuestionnairePage"));
const SkipCleansPage = lazy(() => import("./pages/SkipcleanPage"));
const SkipCleanClient = lazy(() => import("./pages/SkipcleanClient"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const ReservationsPage = lazy(() => import("./pages/ReservationsPage"));
const NettoyagePage = lazy(() => import("./pages/NettoyagePage"));
const RoomServiceOrders = lazy(() => import("./pages/RoomServiceOrders"));

// Client / public pages – lazy loaded
const Home = lazy(() => import("./pages/Home"));
const CommitmentPage = lazy(() => import("./pages/CommitmentPage"));
const SeminairesClient = lazy(() => import("./pages/SeminaireClient"));
const RestaurantsMenusClient = lazy(() => import("./pages/RestaurantsMenusClient"));
const BoissonsClient = lazy(() => import("./pages/BoissonsClient"));
const SpasClient = lazy(() => import("./pages/SpasClient"));
const LoisirsClient = lazy(() => import("./pages/LoisirsClient"));
const EvenementsClient = lazy(() => import("./pages/EvenementsClient"));
const OffresClient = lazy(() => import("./pages/OffresClient"));
const RoomServiceClient = lazy(() => import("./pages/RoomServiceClient"));
const SkyLoungeClient = lazy(() => import("./pages/SkyLoungeClient"));
const AproposClient = lazy(() => import("./pages/AproposClient"));
const PrivacyClient = lazy(() => import("./pages/PrivacyClient"));

const RouteFallback = () => (
  <div style={{
    position: "fixed", inset: 0, background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
    display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontFamily: "system-ui"
  }}>
    <span>Chargement...</span>
  </div>
);

function App() {
  useEffect(() => {
    const el = document.getElementById("initial-loading");
    if (el) el.remove();
  }, []);

  return (
    <BrowserRouter basename="">
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* 🔐 Admin / Authenticated Routes */}
          <Route path="/" element={<LoginPage />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/reservations" element={<ReservationsPage />} />
          <Route path="/nettoyage" element={<NettoyagePage />} />
          <Route path="/roomserviceorders" element={<RoomServiceOrders />} />
          <Route path="/historique" element={<Historique />} />
          <Route path="/statistiques" element={<Statistiques />} />
          <Route path="/page-contents" element={<PageContents />} />
          <Route path="/apropos-admin" element={<AproposAdminPage />} />
          <Route path="/seminaires" element={<SeminairesPage />} />
          <Route path="/restaurants" element={<RestaurantsAndMenusPage />} />
          <Route path="/boissons" element={<BoissonsPage />} />
          <Route path="/loisirs" element={<LoisirsPage />} />
          <Route path="/spas" element={<SpasPage />} />
          <Route path="/evenements" element={<EvenementsPage />} />
          <Route path="/offres" element={<OffresPage />} />
          <Route path="/roomservices" element={<RoomServicePage />} />
          <Route path="/skylounge" element={<SkyLoungePage />} />
          <Route path="/questionnaire-client" element={<QuestionnaireClient />} />
          <Route path="/skipclean-client" element={<SkipCleanClient />} />
          <Route path="/questionnaires" element={<QuestionnairesPage />} />
          <Route path="/skipcleans" element={<SkipCleansPage />} />

          {/* 🌍 Public / Client Routes */}
          <Route path="/home" element={<Home />} />
          <Route path="/commitment" element={<CommitmentPage />} />
          <Route path="/seminaires-client" element={<SeminairesClient />} />
          <Route path="/RestaurantsMenus-client" element={<RestaurantsMenusClient />} />
          <Route path="/boissons-client" element={<BoissonsClient />} />
          <Route path="/spas-client" element={<SpasClient />} />
          <Route path="/loisirs-client" element={<LoisirsClient />} />
          <Route path="/evenements-client" element={<EvenementsClient />} />
          <Route path="/offres-client" element={<OffresClient />} />
          <Route path="/roomservices-client" element={<RoomServiceClient />} />
          <Route path="/skylounge-client" element={<SkyLoungeClient />} />
          <Route path="/apropos-client" element={<AproposClient />} />
          <Route path="/policy-client" element={<PrivacyClient />} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
